import type { ReactNode } from "react";
import { formatTargetLabel } from "./TargetFields";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import {
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
  promotionCtaLabelFa,
  promotionEligibility,
  promotionImageState,
  promotionStateLabelFa,
  type PromotionInventoryRow,
  type PromotionSortKey,
} from "../lib/promotions-ops";
import { cmsStrings } from "../terminology";

/** Muted title line: badge · CTA · image */
function promotionTitleMeta(row: PromotionInventoryRow): string {
  const parts: string[] = [];
  if (row.badge?.trim()) parts.push(row.badge.trim());
  parts.push(promotionCtaLabelFa(row.ctaType, row.ctaLabel));
  parts.push(promotionImageState(row.imagePath).label);
  return parts.join(" · ");
}

function promotionImageFilename(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return trimmed;
  const parts = trimmed.split("/");
  return parts[parts.length - 1] ?? trimmed;
}

type FilterBarProps = {
  listFilter: string;
  onListFilterChange: (value: string) => void;
  queueCount: number;
  query: string;
  onQueryChange: (value: string) => void;
  sort: PromotionSortKey;
  onSortChange: (value: PromotionSortKey) => void;
};

export function PromotionsFilterBar({
  listFilter,
  onListFilterChange,
  queueCount,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: FilterBarProps) {
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label={cmsStrings.inventory.filterStatusAria}
    >
      <div className="filter-toolbar__chips announcements-filter-bar__chips" role="group" aria-label={cmsStrings.inventory.filterStatusAria}>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_ALL ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_ALL)}
          aria-pressed={listFilter === FILTER_ALL}
        >
          {cmsStrings.inventory.filterAll}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_QUEUE ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_QUEUE)}
          aria-pressed={listFilter === FILTER_QUEUE}
        >
          {cmsStrings.inventory.filterQueue}
          {queueCount > 0 && (
            <>
              {" "}
              (
              <LtrIsolate mono>{queueCount}</LtrIsolate>
              )
            </>
          )}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_PAUSED ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_PAUSED)}
          aria-pressed={listFilter === FILTER_PAUSED}
        >
          {cmsStrings.inventory.filterPaused}
        </button>
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">{cmsStrings.promotionsUi.searchAria}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={cmsStrings.promotionsUi.searchPlaceholder}
          aria-label={cmsStrings.promotionsUi.searchAria}
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>{cmsStrings.inventory.sortLabel}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as PromotionSortKey)}
          aria-label={cmsStrings.promotionsUi.sortAria}
        >
          <option value="updated">{cmsStrings.inventory.sortUpdated}</option>
          <option value="title">{cmsStrings.inventory.sortTitle}</option>
          <option value="cta">{cmsStrings.inventory.sortCta}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type InspectProps = {
  item: PromotionInventoryRow;
  actions?: ReactNode;
};

export function PromotionInspectSummary({ item, actions }: InspectProps) {
  const eligibility = promotionEligibility(item);
  const target = formatTargetLabel(item);
  const ui = cmsStrings.promotionsUi;
  const composer = cmsStrings.promotionsComposer;
  const chrome = cmsStrings.composerChrome;
  const stateLabel = promotionStateLabelFa(item.state);
  const showEligibilityDetail =
    eligibility.detail != null && eligibility.detail !== stateLabel;
  const hasAudit =
    Boolean(item.updatedAt) ||
    Boolean(item.submittedAt) ||
    Boolean(item.rejectionReason) ||
    (item.state === "paused" && Boolean(item.pausedFromState));

  return (
    <section
      className="content-inspect content-inspect--promotion"
      aria-labelledby="promotion-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <h2 id="promotion-inspect-title" className="content-inspect__name">
            {item.title}
          </h2>
          <span className={`state-badge state-badge--${item.state}`}>{stateLabel}</span>
        </div>
        <p className="content-inspect__id">
          {cmsStrings.inventory.factId}{" "}
          <LtrIsolate mono>{item.id}</LtrIsolate>
        </p>
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{chrome.sectionBasic}</h3>
        <p className="content-inspect__body">{item.body}</p>
        {item.badge?.trim() ? (
          <p className="content-inspect__inline-fact">
            <span className="content-inspect__inline-label">{ui.factBadge}</span>
            {item.badge}
          </p>
        ) : null}
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{chrome.sectionCtaImage}</h3>
        <dl className="content-inspect__facts content-inspect__facts--stacked">
          <div>
            <dt>{ui.factCta}</dt>
            <dd>{promotionCtaLabelFa(item.ctaType, item.ctaLabel)}</dd>
          </div>
          {item.ctaHref ? (
            <div className="content-inspect__fact--wide">
              <dt>{ui.factCtaUrl}</dt>
              <dd>
                <code className="content-inspect__mono" dir="ltr" title={item.ctaHref}>
                  {item.ctaHref}
                </code>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>{ui.factImagePath}</dt>
            <dd>
              {item.imagePath ? (
                <>
                  <span>{promotionImageFilename(item.imagePath)}</span>
                  <code className="content-inspect__mono content-inspect__mono--subtle" dir="ltr" title={item.imagePath}>
                    {item.imagePath}
                  </code>
                </>
              ) : (
                promotionImageState(null).label
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionPlacement}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{cmsStrings.inventory.factTargeting}</dt>
            <dd>
              {target}
              {item.siteCode ? (
                <>
                  {" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    ({item.siteCode})
                  </bdi>
                </>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>{cmsStrings.inventory.factSchedule}</dt>
            <dd>
              <LtrIsolate mono>
                {formatKabulDisplay(item.startsAt)} – {formatKabulDisplay(item.endsAt)}
              </LtrIsolate>
            </dd>
          </div>
          <div>
            <dt>{cmsStrings.inventory.factPriority}</dt>
            <dd>
              <LtrIsolate mono>{item.priority}</LtrIsolate>
            </dd>
          </div>
          <div>
            <dt>{ui.factEligibility}</dt>
            <dd>
              {eligibility.label}
              {showEligibilityDetail ? (
                <span className="content-inspect__detail"> — {eligibility.detail}</span>
              ) : null}
            </dd>
          </div>
        </dl>
      </div>

      {hasAudit ? (
        <div className="content-inspect__block content-inspect__block--audit">
          <h3 className="content-inspect__block-title">{ui.inspectSectionAudit}</h3>
          <dl className="content-inspect__facts content-inspect__facts--pair">
            {item.updatedAt ? (
              <div>
                <dt>{cmsStrings.inventory.factUpdated}</dt>
                <dd>
                  <LtrIsolate mono>{formatKabulDisplay(item.updatedAt)}</LtrIsolate>
                </dd>
              </div>
            ) : null}
            {item.submittedAt ? (
              <div>
                <dt>{ui.factSubmitted}</dt>
                <dd>
                  <LtrIsolate mono>{formatKabulDisplay(item.submittedAt)}</LtrIsolate>
                </dd>
              </div>
            ) : null}
            {item.rejectionReason ? (
              <div className="content-inspect__fact--wide">
                <dt>{composer.rejectReason}</dt>
                <dd>{item.rejectionReason}</dd>
              </div>
            ) : null}
            {item.state === "paused" && item.pausedFromState ? (
              <div>
                <dt>{ui.factPausedFrom}</dt>
                <dd>{promotionStateLabelFa(item.pausedFromState)}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      ) : null}

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

type ListProps = {
  rows: PromotionInventoryRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: PromotionInventoryRow) => ReactNode;
};

export function PromotionsInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: ListProps) {
  const ui = cmsStrings.promotionsUi;

  return (
    <>
      <div className="sites-inventory-table-wrap inventory-table-wrap" role="region" aria-label={ui.tableAria}>
        <table className="data-table content-inventory-table inventory-table promotions-inventory-table">
          <thead>
            <tr>
              <th scope="col">{cmsStrings.inventory.sortTitle}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colTarget}</th>
              <th scope="col">{cmsStrings.inventory.factSchedule}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                      aria-label={`${cmsStrings.inventory.details}: ${row.title}`}
                    >
                      {row.title}
                    </button>
                    <div className="sites-inventory__muted">{promotionTitleMeta(row)}</div>
                  </td>
                  <td>
                    <span className={`state-badge state-badge--${row.state}`}>
                      {promotionStateLabelFa(row.state)}
                    </span>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={formatTargetLabel(row)}>
                      {formatTargetLabel(row)}
                    </div>
                    {row.siteCode && (
                      <div className="sites-inventory__muted inventory-cell-truncate" title={row.siteCode}>
                        <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={`${formatKabulDisplay(row.startsAt)} – ${formatKabulDisplay(row.endsAt)}`}>
                      <LtrIsolate mono>{formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}</LtrIsolate>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="sites-inventory-cards content-inventory-cards inventory-cards" aria-label={ui.cardsAria}>
        {rows.map((row) => {
          const selected = row.id === selectedId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
                aria-label={`${cmsStrings.inventory.details}: ${row.title}`}
              >
                <span className="sites-inventory-card__title">{row.title}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge state-badge--${row.state}`}>
                    {promotionStateLabelFa(row.state)}
                  </span>
                </span>
                <span className="sites-inventory__muted">{promotionTitleMeta(row)}</span>
                <span className="sites-inventory__muted">
                  {ui.colTarget}: {formatTargetLabel(row)}
                </span>
                <span className="sites-inventory__muted">
                  {cmsStrings.inventory.factSchedule}:{" "}
                  <LtrIsolate mono>{formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}</LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
